import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const UpdaterContext = createContext();

export const useUpdater = () => {
  const context = useContext(UpdaterContext);
  if (!context) {
    throw new Error('useUpdater must be used within UpdaterProvider');
  }
  return context;
};

export const UpdaterProvider = ({ children }) => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');

  // Use the same approach as your login page
  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  useEffect(() => {
    if (!isElectron()) return;

    // Get current app version
    const getAppVersion = async () => {
      try {
        const version = await ipcRenderer.invoke('get-app-version');
        console.log("hereeee", version)
        setCurrentVersion(version);
      } catch (error) {
        console.error('Error getting app version:', error);
      }
    };

    getAppVersion();

    // Set up event listeners
    const handleUpdateAvailable = (event, info) => {
      console.log('Update available:', info);
      setUpdateInfo(info);
      setUpdateAvailable(true);
      setShowUpdateModal(true);
    };

    const handleUpdateNotAvailable = (event, info) => {
      console.log('No update available:', info);
    };

    const handleDownloadProgress = (event, progress) => {
      setDownloadProgress(Math.round(progress.percent));
    };

    const handleUpdateDownloaded = (event, info) => {
      console.log('Update downloaded:', info);
      setIsDownloading(false);
      // Show install dialog
      showInstallDialog();
    };

    const handleUpdateError = (event, error) => {
      console.error('Update error:', error);
      setIsDownloading(false);
      toast.error('Update failed. Please try again later.');
    };

    // Add event listeners
    ipcRenderer.on('update-available', handleUpdateAvailable);
    ipcRenderer.on('update-not-available', handleUpdateNotAvailable);
    ipcRenderer.on('download-progress', handleDownloadProgress);
    ipcRenderer.on('update-downloaded', handleUpdateDownloaded);
    ipcRenderer.on('update-error', handleUpdateError);

    // Cleanup listeners on unmount
    return () => {
      ipcRenderer.removeListener('update-available', handleUpdateAvailable);
      ipcRenderer.removeListener('update-not-available', handleUpdateNotAvailable);
      ipcRenderer.removeListener('download-progress', handleDownloadProgress);
      ipcRenderer.removeListener('update-downloaded', handleUpdateDownloaded);
      ipcRenderer.removeListener('update-error', handleUpdateError);
    };
  }, []);

  const checkForUpdates = async () => {
    if (isElectron()) {
      try {
        await ipcRenderer.invoke('check-for-updates');
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }
  };

  const downloadUpdate = async () => {
    if (isElectron()) {
      try {
        setIsDownloading(true);
        setDownloadProgress(0);
        await ipcRenderer.invoke('download-update');
      } catch (error) {
        console.error('Error downloading update:', error);
        setIsDownloading(false);
      }
    }
  };

  const installUpdate = async () => {
    if (isElectron()) {
      try {
        await ipcRenderer.invoke('install-update');
      } catch (error) {
        console.error('Error installing update:', error);
      }
    }
  };

  const showInstallDialog = () => {
    const installModal = document.createElement('div');
    installModal.innerHTML = `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100">
          <div class="p-6 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-800 mb-2">Update Downloaded!</h3>
            <p class="text-gray-600 mb-6">The update has been downloaded successfully. Click install to restart the app with the latest version.</p>
            <div class="flex gap-3">
              <button id="install-later" class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Install Later
              </button>
              <button id="install-now" class="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-colors">
                Install Now
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(installModal);

    document.getElementById('install-now').onclick = () => {
      document.body.removeChild(installModal);
      installUpdate();
    };

    document.getElementById('install-later').onclick = () => {
      document.body.removeChild(installModal);
    };
  };

  const contextValue = {
    updateInfo,
    downloadProgress,
    isDownloading,
    updateAvailable,
    showUpdateModal,
    setShowUpdateModal,
    currentVersion,
    checkForUpdates,
    downloadUpdate,
    installUpdate,
  };

  return (
    <UpdaterContext.Provider value={contextValue}>
      {children}
      {showUpdateModal && <UpdateModal />}
    </UpdaterContext.Provider>
  );
};

const UpdateModal = () => {
  const { 
    updateInfo, 
    downloadProgress, 
    isDownloading, 
    setShowUpdateModal, 
    downloadUpdate,
    currentVersion 
  } = useUpdater();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100">
        {!isDownloading ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Update Available!</h3>
              <p className="text-gray-600 mb-4">
                A new version ({updateInfo?.version}) is available.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                Current version: {currentVersion}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={downloadUpdate}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Update
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Downloading Update...</h3>
              <p className="text-gray-600 mb-4">
                Please wait while we download the latest version , It may take a while.
              </p>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{downloadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300 relative overflow-hidden"
                  style={{ width: `${downloadProgress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowUpdateModal(false)}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hide Progress
            </button>
          </div>
        )}
      </div>
    </div>
  );
};